import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Table,
} from "reactstrap";
import { Puff } from "react-loader-spinner";
import Switch from "react-switch";
import { AdminService } from "services/AdminService";
import {
  StoreDefaultService,
  StoreDeliveryService,
  StorePricingCatalog,
} from "models/StoreDeliveryService";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

interface Props {
  integrationId: string;
}

const emptyCatalog = (): StorePricingCatalog => ({
  defaultServices: [],
  storeServices: [],
  hasActiveStoreServices: false,
});

const serviceId = (service: StoreDeliveryService): string =>
  String(service.id || service._id || "");

const EMPTY_CELL = "—";

const isClockTime = (value?: string): boolean =>
  /^([01]\d|2[0-3]):[0-5]\d$/.test(value || "");

const clockToPickerDate = (value?: string): Date | null => {
  if (!isClockTime(value)) return null;
  const [hours, minutes] = String(value).split(":").map(Number);
  return new Date(2000, 0, 1, hours, minutes, 0, 0);
};

const pickerDateToClock = (value: Date | null): string => {
  if (!value) return "";
  return `${String(value.getHours()).padStart(2, "0")}:${String(
    value.getMinutes()
  ).padStart(2, "0")}`;
};

const clockAfter = (value?: string): Date => {
  const start = clockToPickerDate(value) || new Date(2000, 0, 1, 0, 0, 0, 0);
  return new Date(start.getTime() + 60 * 1000);
};

const ClockTimeField: React.FC<{
  id: string;
  value: string;
  disabled?: boolean;
  minTime?: Date;
  maxTime?: Date;
  onChange: (value: string) => void;
}> = ({ id, value, disabled, minTime, maxTime, onChange }) => {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="store-clock-picker"
      onClick={event => {
        if (disabled) return;
        const target = event.target as HTMLElement;
        if (target.closest(".react-datepicker-popper")) return;
        setOpen(true);
      }}
    >
      <DatePicker
        id={id}
        open={open}
        onInputClick={() => {
          if (!disabled) setOpen(true);
        }}
        onClickOutside={() => setOpen(false)}
        onFocus={event => event.target.blur()}
        selected={clockToPickerDate(value)}
        onChange={next => {
          onChange(pickerDateToClock(next));
          setOpen(false);
        }}
        showTimeSelect
        showTimeSelectOnly
        timeFormat="HH:mm"
        timeIntervals={15}
        dateFormat="HH:mm"
        timeCaption="Time"
        placeholderText="Select time"
        className="form-control"
        disabled={disabled}
        minTime={minTime}
        maxTime={maxTime}
        popperClassName="store-clock-picker__popper"
        popperProps={{ strategy: "fixed" }}
      />
    </div>
  );
};

const displayTime = (value?: string): string => {
  if (!value || typeof value !== "string") return EMPTY_CELL;
  const match = value.match(/^([01]\d|2[0-3]):([0-5]\d)$/);
  if (!match) return EMPTY_CELL;
  return `${match[1]}:${match[2]}`;
};

const displayWindow = (start?: string, end?: string): string => {
  const from = displayTime(start);
  const to = displayTime(end);
  if (from === EMPTY_CELL || to === EMPTY_CELL) return EMPTY_CELL;
  return `${from} – ${to}`;
};

const money = (value?: number): string =>
  new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
  }).format(Number.isFinite(value) ? Number(value) : 0);

const DetailItem: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <Col md={6} className="mb-3">
    <div className="text-muted font-size-12">{label}</div>
    <div className="text-break">{children}</div>
  </Col>
);

const ServiceDetails: React.FC<{ service: StoreDeliveryService }> = ({
  service,
}) => {
  const pricing = service.pricing;
  const areas = service.territoryAreas || [];
  const vehicles = pricing?.vehicleSurcharges;

  return (
    <>
      <Row>
        <DetailItem label="Service name">
          {service.name?.trim() || EMPTY_CELL}
        </DetailItem>
        <DetailItem label="Cutoff">
          {displayTime(service.cutoffTime)}
        </DetailItem>
        <DetailItem label="Delivery window">
          {displayWindow(
            service.deliveryWindowStart,
            service.deliveryWindowEnd
          )}
        </DetailItem>
        <Col xs={12} className="mb-3">
          <div className="text-muted font-size-12">Description</div>
          <div className="text-break" style={{ whiteSpace: "pre-line" }}>
            {service.description?.trim() || EMPTY_CELL}
          </div>
        </Col>
      </Row>

      <h6 className="mb-3">Pricing</h6>
      {!pricing ? (
        <p className="text-muted">Pricing is not set.</p>
      ) : (
        <Row>
          <DetailItem label="Flat price">
            {pricing.flatPricingEnabled ? (
              <>
                <div>{money(pricing.flatPrice)}</div>
                <span className="text-muted d-block">
                  Includes {pricing.includedDistanceKm} km
                </span>
                <span className="text-muted d-block">
                  {money(pricing.additionalKmPrice)} / additional km
                </span>
              </>
            ) : (
              <span className="text-muted">Disabled</span>
            )}
          </DetailItem>
          <DetailItem label="Vehicle charges">
            {pricing.vehicleChargesEnabled ? (
              <>
                Car {money(vehicles?.car)} · SUV {money(vehicles?.suv)} ·
                Minivan {money(vehicles?.minivan)}
              </>
            ) : (
              <span className="text-muted">Disabled</span>
            )}
          </DetailItem>
          <DetailItem label="Weight charges">
            {pricing.weightChargesEnabled ? (
              <>
                {pricing.freeWeightUpToLb} lb free ·{" "}
                {money(pricing.additionalPricePerLb)} / lb
              </>
            ) : (
              <span className="text-muted">Disabled</span>
            )}
          </DetailItem>
          <DetailItem label="Tax">
            {pricing.taxEnabled ? (
              Number.isFinite(pricing.hst) ? `HST ${pricing.hst}%` : "On"
            ) : (
              <span className="text-muted">Disabled</span>
            )}
          </DetailItem>
        </Row>
      )}

      <h6 className="mb-3">Territory</h6>
      {areas.length === 0 ? (
        <p className="text-muted mb-0">No areas selected.</p>
      ) : (
        <div className="table-responsive border rounded">
          <Table className="align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th>Area / City</th>
                <th>Postal codes</th>
                <th className="text-end">Surcharge</th>
              </tr>
            </thead>
            <tbody>
              {areas.map(area => (
                <tr key={area.areaKey || area.label}>
                  <td className="fw-medium">
                    {area.label?.trim() || EMPTY_CELL}
                  </td>
                  <td style={{ whiteSpace: "normal" }}>
                    {(area.fsaCodes || []).filter(Boolean).join(", ") ||
                      EMPTY_CELL}
                  </td>
                  <td className="text-end">{money(area.surchargeCad)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </>
  );
};

const StoreServicesTab: React.FC<Props> = ({ integrationId }) => {
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState<StorePricingCatalog>(emptyCatalog());
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [viewing, setViewing] = useState<StoreDeliveryService | null>(null);
  const [viewingDefault, setViewingDefault] = useState<StoreDefaultService | null>(null);
  const [editingDefault, setEditingDefault] = useState<StoreDefaultService | null>(null);
  const [defaultDraft, setDefaultDraft] = useState({
    name: "",
    description: "",
    cutoffTime: "",
    deliveryWindowStart: "",
    deliveryWindowEnd: "",
  });
  const [savingDefault, setSavingDefault] = useState(false);

  const applyCatalog = (data: StorePricingCatalog | StoreDeliveryService[]) => {
    if (Array.isArray(data)) {
      setCatalog({
        defaultServices: [],
        storeServices: data,
        hasActiveStoreServices: data.some(service => service.status === "active"),
      });
      return;
    }
    setCatalog({
      defaultServices: data.defaultServices || [],
      storeServices: data.storeServices || [],
      hasActiveStoreServices: Boolean(data.hasActiveStoreServices),
    });
  };

  const loadServices = useCallback(async () => {
    setLoading(true);
    try {
      const response = await AdminService.getStorePricingCatalog(integrationId);
      if (!response.success || !response.data) {
        throw new Error(response.message || "Unable to load services.");
      }
      applyCatalog(response.data);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to load store services."
      );
    } finally {
      setLoading(false);
    }
  }, [integrationId]);

  useEffect(() => {
    loadServices();
  }, [loadServices]);

  const createService = () => {
    navigate(`/super_admin/stores/${integrationId}/services/new/details`);
  };

  const releaseButtonFocus = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.currentTarget.blur();
  };

  const clearReturnedFocus = () => {
    window.setTimeout(() => {
      const active = document.activeElement;
      if (active instanceof HTMLElement) {
        active.blur();
      }
    }, 0);
  };

  const closeDefaultView = () => {
    setViewingDefault(null);
    clearReturnedFocus();
  };

  const closeServiceView = () => {
    setViewing(null);
    clearReturnedFocus();
  };

  const openDefaultEditor = (service: StoreDefaultService) => {
    setEditingDefault(service);
    setDefaultDraft({
      name: service.name || "",
      description: service.description || "",
      cutoffTime: service.cutoffTime || "",
      deliveryWindowStart: service.deliveryWindowStart || "",
      deliveryWindowEnd: service.deliveryWindowEnd || "",
    });
  };

  const saveDefaultService = async () => {
    if (!editingDefault) return;
    const name = defaultDraft.name.trim();
    const description = defaultDraft.description.trim();
    if (!name || !description) {
      toast.error("Service name and description are required.");
      return;
    }
    setSavingDefault(true);
    try {
      const response = await AdminService.updateStoreDefaultService(
        integrationId,
        editingDefault.settingsType,
        {
          name,
          description,
          cutoffTime: defaultDraft.cutoffTime,
          deliveryWindowStart: defaultDraft.deliveryWindowStart,
          deliveryWindowEnd: defaultDraft.deliveryWindowEnd,
        }
      );
      if (!response.success || !response.data) {
        throw new Error(response.message || "Unable to update this service.");
      }
      applyCatalog(response.data);
      setEditingDefault(null);
      toast.success("Default service updated.");
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to update this service."
      );
    } finally {
      setSavingDefault(false);
    }
  };

  const toggleDefaultService = async (
    service: StoreDefaultService,
    enabled: boolean
  ) => {
    const key = `default:${service.settingsType}`;
    setBusyKey(key);
    try {
      const response = await AdminService.toggleStoreDefaultService(
        integrationId,
        service.settingsType,
        enabled
      );
      if (!response.success || !response.data) {
        throw new Error(response.message || "Unable to update this service.");
      }
      applyCatalog(response.data);
      toast.success(
        enabled
          ? `${service.name} is active for this store.`
          : `${service.name} will not appear at checkout for this store.`
      );
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to update this service."
      );
    } finally {
      setBusyKey(null);
    }
  };

  const toggleCreatedService = async (
    service: StoreDeliveryService,
    enabled: boolean
  ) => {
    const id = serviceId(service);
    if (!id) return;
    const key = `store:${id}`;
    setBusyKey(key);
    try {
      const response = enabled
        ? await AdminService.activateStoreDeliveryService(integrationId, id)
        : await AdminService.deactivateStoreDeliveryService(integrationId, id);
      if (!response.success || !response.data) {
        throw new Error(
          response.message ||
            (enabled
              ? "Unable to activate this service."
              : "Unable to deactivate this service.")
        );
      }
      const updated = response.data;
      setCatalog(current => {
        const storeServices = current.storeServices.map(item =>
          serviceId(item) === id ? { ...item, ...updated } : item
        );
        return {
          ...current,
          storeServices,
          hasActiveStoreServices: storeServices.some(
            item => item.status === "active"
          ),
        };
      });
      toast.success(
        enabled
          ? `${service.name} is active for this store.`
          : `${service.name} will not appear at checkout for this store.`
      );
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to update this service."
      );
    } finally {
      setBusyKey(null);
    }
  };

  const activeCreatedCount = catalog.storeServices.filter(
    service => service.status === "active"
  ).length;
  const enabledDefaultCount = catalog.defaultServices.filter(
    service => service.enabledForStore && service.globallyActive
  ).length;
  const checkoutHasRates =
    activeCreatedCount > 0 ||
    (activeCreatedCount === 0 && enabledDefaultCount > 0);

  return (
    <>
      <div className="d-flex align-items-start justify-content-between mb-4">
        <div>
          <h5 className="font-size-15 mb-1">Services</h5>
          <p className="text-muted mb-0">
            Activate or deactivate the default Zulu services and any services
            created for this store.
          </p>
        </div>
        <Button color="primary" onClick={createService}>
          <i className="bx bx-plus me-1" />
          Create Service
        </Button>
      </div>

      {loading ? (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "240px" }}
        >
          <Puff color="#FFD0B8" height={50} width={50} />
        </div>
      ) : (
        <>
          <h6 className="font-size-14 mb-2">Default Zulu services</h6>
          <p className="text-muted font-size-13 mb-3">
            These platform services stay on checkout while their switch is on.
            An active custom service is added beside them.
          </p>
          <div className="table-responsive border rounded">
            <Table className="table-nowrap align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>Service Name</th>
                  <th>Status</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {catalog.defaultServices.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="text-center text-muted py-4">
                      Default Zulu services are not configured.
                    </td>
                  </tr>
                ) : (
                  catalog.defaultServices.map(service => {
                    const key = `default:${service.settingsType}`;
                    return (
                      <tr key={service.settingsType}>
                        <td>
                          <div className="fw-medium">{service.name}</div>
                          <div className="text-muted font-size-12">
                            {service.description}
                            {!service.globallyActive
                              ? " Off in Settings."
                              : ""}
                          </div>
                        </td>
                        <td>
                          <div className="d-flex align-items-center">
                            {busyKey === key && (
                              <Puff
                                color="#FFD0B8"
                                height={18}
                                width={18}
                                wrapperClass="me-2"
                              />
                            )}
                            <Switch
                              checked={service.enabledForStore}
                              disabled={busyKey !== null}
                              onChange={enabled =>
                                toggleDefaultService(service, enabled)
                              }
                              onColor="#F75D21"
                              offColor="#CED4DA"
                              width={50}
                              height={20}
                              handleDiameter={15}
                              checkedIcon={<></>}
                              uncheckedIcon={<></>}
                              aria-label={`Toggle checkout for ${
                                service.name || "this service"
                              }`}
                            />
                            <span className="text-muted font-size-13 ms-2">
                              {service.enabledForStore ? "Enable" : "Disable"}
                            </span>
                          </div>
                        </td>
                        <td className="text-end">
                          <div className="d-inline-flex align-items-center gap-2">
                            <Button
                              type="button"
                              color="light"
                              size="sm"
                              aria-label={`View ${service.name || "service"}`}
                              title="View"
                              disabled={busyKey !== null}
                              onClick={(event) => {
                                releaseButtonFocus(event);
                                setViewingDefault(service);
                              }}
                            >
                              <i className="bx bx-show font-size-16" />
                            </Button>
                            <Button
                              color="light"
                              size="sm"
                              disabled={busyKey !== null}
                              onClick={() => openDefaultEditor(service)}
                            >
                              Edit
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>

          {catalog.hasActiveStoreServices && (
            <Alert color="info" className="mt-3 mb-0">
              Active custom services are offered with the default services
              that are switched on, for the same delivery area.
            </Alert>
          )}

          <div className="d-flex align-items-start justify-content-between mt-5 mb-3">
            <div>
              <h6 className="font-size-14 mb-1">Created services</h6>
              <p className="text-muted font-size-13 mb-0">
                Custom services created for this store. An active custom
                service is listed at checkout next to the default services
                that are switched on.
              </p>
            </div>
          </div>

          <div className="table-responsive border rounded">
            <Table className="table-nowrap align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>Service name</th>
                  <th>Cutoff</th>
                  <th>Delivery window</th>
                  <th>Status</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {catalog.storeServices.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center text-muted py-4">
                      No custom services created for this store.
                    </td>
                  </tr>
                ) : (
                  catalog.storeServices.map((service, index) => {
                    const id = serviceId(service);
                    const key = id ? `store:${id}` : `store-row-${index}`;
                    const isActive = service.status === "active";
                    const canToggle = Boolean(id) && service.status !== "draft";
                    return (
                      <tr key={key}>
                        <td className="fw-medium">
                          {service.name || EMPTY_CELL}
                        </td>
                        <td>{displayTime(service.cutoffTime)}</td>
                        <td>
                          {displayWindow(
                            service.deliveryWindowStart,
                            service.deliveryWindowEnd
                          )}
                        </td>
                        <td>
                          <div className="d-flex align-items-center">
                            {busyKey === key && (
                              <Puff
                                color="#FFD0B8"
                                height={18}
                                width={18}
                                wrapperClass="me-2"
                              />
                            )}
                            <Switch
                              checked={isActive}
                              disabled={!canToggle || busyKey !== null}
                              onChange={enabled =>
                                toggleCreatedService(service, enabled)
                              }
                              onColor="#F75D21"
                              offColor="#CED4DA"
                              width={50}
                              height={20}
                              handleDiameter={15}
                              checkedIcon={<></>}
                              uncheckedIcon={<></>}
                              aria-label={`Toggle checkout for ${
                                service.name || "this service"
                              }`}
                            />
                            <span className="text-muted font-size-13 ms-2">
                              {isActive ? "Enable" : "Disable"}
                            </span>
                          </div>
                        </td>
                        <td className="text-end">
                          <div className="d-inline-flex align-items-center gap-2">
                            <Button
                              type="button"
                              color="light"
                              size="sm"
                              aria-label={`View ${service.name || "service"}`}
                              title="View"
                              onClick={(event) => {
                                releaseButtonFocus(event);
                                setViewing(service);
                              }}
                            >
                              <i className="bx bx-show font-size-16" />
                            </Button>
                            <Button
                              color="light"
                              size="sm"
                              disabled={!id}
                              onClick={() =>
                                navigate(
                                  `/super_admin/stores/${integrationId}/services/${id}/details`
                                )
                              }
                            >
                              Edit
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>
        </>
      )}

      <Modal
        isOpen={viewingDefault !== null}
        toggle={closeDefaultView}
        returnFocusAfterClose={false}
        centered
        size="lg"
      >
        <ModalHeader toggle={closeDefaultView}>
          {viewingDefault?.name?.trim() || "Service details"}
        </ModalHeader>
        <ModalBody>
          {viewingDefault && (
            <Row>
              <DetailItem label="Service name">
                {viewingDefault.name?.trim() || EMPTY_CELL}
              </DetailItem>
              <DetailItem label="Status">
                {viewingDefault.enabledForStore ? "Enable" : "Disable"}
                {!viewingDefault.globallyActive ? " · Off in Settings" : ""}
              </DetailItem>
              <DetailItem label="Cutoff">
                {displayTime(viewingDefault.cutoffTime)}
              </DetailItem>
              <DetailItem label="Delivery window">
                {displayWindow(
                  viewingDefault.deliveryWindowStart,
                  viewingDefault.deliveryWindowEnd
                )}
              </DetailItem>
              <Col xs={12} className="mb-3">
                <div className="text-muted font-size-12">Description</div>
                <div className="text-break" style={{ whiteSpace: "pre-line" }}>
                  {viewingDefault.description?.trim() || EMPTY_CELL}
                </div>
              </Col>
            </Row>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={closeDefaultView}>
            Close
          </Button>
          <Button
            color="primary"
            disabled={!viewingDefault}
            onClick={() => {
              if (!viewingDefault) return;
              const service = viewingDefault;
              setViewingDefault(null);
              clearReturnedFocus();
              openDefaultEditor(service);
            }}
          >
            Edit
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={editingDefault !== null}
        toggle={() => !savingDefault && setEditingDefault(null)}
        centered
      >
        <ModalHeader toggle={() => !savingDefault && setEditingDefault(null)}>
          Edit {editingDefault?.name || "default service"}
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>Service name</Label>
            <Input
              value={defaultDraft.name}
              maxLength={80}
              onChange={event =>
                setDefaultDraft(current => ({
                  ...current,
                  name: event.target.value,
                }))
              }
            />
          </FormGroup>
          <FormGroup>
            <Label>Description</Label>
            <Input
              type="textarea"
              rows={3}
              value={defaultDraft.description}
              maxLength={240}
              onChange={event =>
                setDefaultDraft(current => ({
                  ...current,
                  description: event.target.value,
                }))
              }
            />
          </FormGroup>
          <FormGroup>
            <Label>Cutoff time</Label>
            <ClockTimeField
              id="default-cutoff-time"
              value={defaultDraft.cutoffTime}
              disabled={savingDefault}
              onChange={cutoffTime =>
                setDefaultDraft(current => ({ ...current, cutoffTime }))
              }
            />
          </FormGroup>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>Delivery window start</Label>
                <ClockTimeField
                  id="default-window-start"
                  value={defaultDraft.deliveryWindowStart}
                  disabled={savingDefault}
                  onChange={deliveryWindowStart =>
                    setDefaultDraft(current => ({
                      ...current,
                      deliveryWindowStart,
                    }))
                  }
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Delivery window end</Label>
                <ClockTimeField
                  id="default-window-end"
                  value={defaultDraft.deliveryWindowEnd}
                  disabled={savingDefault}
                  minTime={
                    isClockTime(defaultDraft.deliveryWindowStart)
                      ? clockAfter(defaultDraft.deliveryWindowStart)
                      : undefined
                  }
                  maxTime={
                    isClockTime(defaultDraft.deliveryWindowStart)
                      ? new Date(2000, 0, 1, 23, 59, 0, 0)
                      : undefined
                  }
                  onChange={deliveryWindowEnd => {
                    if (
                      isClockTime(defaultDraft.deliveryWindowStart) &&
                      deliveryWindowEnd <= defaultDraft.deliveryWindowStart
                    ) {
                      return;
                    }
                    setDefaultDraft(current => ({
                      ...current,
                      deliveryWindowEnd,
                    }));
                  }}
                />
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button
            color="light"
            disabled={savingDefault}
            onClick={() => setEditingDefault(null)}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            disabled={savingDefault}
            onClick={saveDefaultService}
          >
            Save
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={viewing !== null}
        toggle={closeServiceView}
        returnFocusAfterClose={false}
        centered
        size="lg"
      >
        <ModalHeader toggle={closeServiceView}>
          {viewing?.name?.trim() || "Service details"}
        </ModalHeader>
        <ModalBody>
          {viewing && <ServiceDetails service={viewing} />}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={closeServiceView}>
            Close
          </Button>
          <Button
            color="primary"
            disabled={!viewing || !serviceId(viewing)}
            onClick={() => {
              if (!viewing) return;
              const id = serviceId(viewing);
              setViewing(null);
              clearReturnedFocus();
              navigate(
                `/super_admin/stores/${integrationId}/services/${id}/details`
              );
            }}
          >
            Edit
          </Button>
        </ModalFooter>
      </Modal>

      {!loading && !checkoutHasRates && (
        <Alert color="warning" className="mt-4 mb-0">
          <div className="d-flex">
            <i className="bx bx-error-circle font-size-20 me-3 mt-1" />
            <div>
              <h6 className="alert-heading mb-1">
                No services are active for this store
              </h6>
              <span>
                Turn on a default Zulu service or activate a created service to
                offer delivery options at checkout.
              </span>
            </div>
          </div>
        </Alert>
      )}
    </>
  );
};

export default StoreServicesTab;
